/**
 * Deal Kanban Board — drag-and-drop columns for pipeline stages.
 * Uses @dnd-kit for accessible, composable drag-and-drop.
 */

import { useCallback, useMemo } from 'react'
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { useState } from 'react'
import { useCrmStore } from '@/stores/crmStore'
import { useCRM } from '@/hooks/useCRM'
import { formatCents } from '@/utils/currency'
import { DealCard } from '@/components/crm/DealCard'
import { Spinner, EmptyState } from '@/components/ui'
import type { Pipeline, Deal } from '@/types/crm'

interface DealKanbanProps {
  pipeline: Pipeline
}

export function DealKanban({ pipeline }: DealKanbanProps) {
  const deals = useCrmStore((s) => s.deals)
  const isLoading = useCrmStore((s) => s.isLoadingDeals)
  const stats = useCrmStore((s) => s.pipelineStats)
  const { moveDealStage } = useCRM()
  const [activeDragId, setActiveDragId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  )

  // Group deals by stage
  const dealsByStage = useMemo(() => {
    const map = new Map<string, Deal[]>()
    for (const stage of pipeline.stages) {
      map.set(stage.id, [])
    }
    for (const deal of deals) {
      const list = map.get(deal.stageId)
      if (list) list.push(deal)
    }
    return map
  }, [deals, pipeline.stages])

  const activeDeal = activeDragId ? deals.find((d) => d.id === activeDragId) : null

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string)
  }, [])

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setActiveDragId(null)
    const { active, over } = event
    if (!over) return

    const dealId = active.id as string
    const targetStageId = over.id as string

    // Find the deal
    const deal = deals.find((d) => d.id === dealId)
    if (!deal || deal.stageId === targetStageId) return

    // Optimistically update the store
    useCrmStore.getState().updateDealInList({
      ...deal,
      stageId: targetStageId,
    })

    // Persist
    const result = await moveDealStage(dealId, targetStageId)
    if (!result) {
      // Revert on failure
      useCrmStore.getState().updateDealInList(deal)
    }
  }, [deals, moveDealStage])

  if (isLoading && deals.length === 0) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    )
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 p-4 h-full overflow-x-auto">
        {pipeline.stages.filter((s) => !s.isLost).map((stage) => {
          const stageDeals = dealsByStage.get(stage.id) || []
          const stageStats = stats?.stages.find((s) => s.stageId === stage.id)

          return (
            <KanbanColumn
              key={stage.id}
              stageId={stage.id}
              stageName={stage.name}
              stageColor={stage.color}
              deals={stageDeals}
              count={stageStats?.count || stageDeals.length}
              weightedCents={stageStats?.weightedCents || 0}
              currency={pipeline.currency}
            />
          )
        })}

        {/* Lost column (collapsed) */}
        {pipeline.stages.filter((s) => s.isLost).map((stage) => {
          const stageDeals = dealsByStage.get(stage.id) || []
          return (
            <KanbanColumn
              key={stage.id}
              stageId={stage.id}
              stageName={stage.name}
              stageColor={stage.color}
              deals={stageDeals}
              count={stageDeals.length}
              weightedCents={0}
              currency={pipeline.currency}
              collapsed={stageDeals.length === 0}
            />
          )
        })}
      </div>

      <DragOverlay>
        {activeDeal ? <DealCard deal={activeDeal} isDragging /> : null}
      </DragOverlay>
    </DndContext>
  )
}

// ── Kanban Column ───────────────────────────────────────────────────────────

interface KanbanColumnProps {
  stageId: string
  stageName: string
  stageColor: string
  deals: Deal[]
  count: number
  weightedCents: number
  currency: string | null
  collapsed?: boolean
}

function KanbanColumn({
  stageId, stageName, stageColor, deals, count, weightedCents, currency, collapsed,
}: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: stageId })

  if (collapsed) {
    return (
      <div className="w-48 shrink-0 opacity-50">
        <div className="flex items-center gap-2 mb-2 px-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stageColor }} />
          <span className="text-xs font-medium text-muted-foreground">{stageName}</span>
          <span className="text-xs text-muted-foreground">({count})</span>
        </div>
      </div>
    )
  }

  return (
    <div className="w-72 shrink-0 flex flex-col">
      {/* Column header */}
      <div className="flex items-center gap-2 mb-2 px-2">
        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stageColor }} />
        <span className="text-sm font-medium text-foreground">{stageName}</span>
        <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">{count}</span>
        {weightedCents > 0 && (
          <span className="text-xs text-muted-foreground ml-auto">{formatCents(weightedCents, currency)}</span>
        )}
      </div>

      {/* Cards container — droppable */}
      <div
        ref={setNodeRef}
        className={`flex-1 space-y-2 p-1.5 rounded-lg min-h-[100px] transition-colors ${
          isOver ? 'bg-info/10 border-2 border-dashed border-info' : 'bg-muted/50'
        }`}
      >
        {deals.map((deal) => (
          <DealCard key={deal.id} deal={deal} />
        ))}
        {deals.length === 0 && !isOver && (
          <EmptyState title="No deals in this stage" className="py-4" />
        )}
      </div>
    </div>
  )
}
